import React, { useEffect, useMemo, useRef } from "react"
import anime from "animejs"
import classNames from "classnames"
import { usePresence } from "motion/react"

import { useTranslation } from "@/locales"
import Toast from "@/topic/rogue_5/components/Toast"
import { gameSignIn } from "@/topic/rogue_5/service"
import { getStartOfDay, sleep } from "@/topic/rogue_5/utils/misc"
import { SFX } from "@/topic/rogue_5/utils/sfx"
import MainButton from "../../components/MainButton"
import { useResultModal } from "../../components/Modal/ResultModal"
import { EGameScene, useGameStore, useShowBoxButton } from "../../store"

import labelPng from "../../../../assets/img/game/record/label.png"
import labelWebP from "../../../../assets/img/game/record/label.webp"
import layer3Png from "../../../../assets/img/game/record/layer3.png"
import layer3WebP from "../../../../assets/img/game/record/layer3.webp"
import styles from "./index.module.less"

const SCENE_ANIMATION = {
    animate: async (container: HTMLDivElement) => {
        SFX.play("recordModal")
        const buttonGroup = container.querySelector(
            `.${styles.buttonGroup}`
        ) as HTMLDivElement
        const layer2 = container.querySelector(
            `.${styles.layer2}`
        ) as HTMLDivElement
        const layer3 = container.querySelector(
            `.${styles.layer3}`
        ) as HTMLDivElement
        const contentContainer = container.querySelector(
            `.${styles.contentContainer}`
        ) as HTMLDivElement
        const label = container.querySelector(
            `.${styles.label}`
        ) as HTMLDivElement
        const coins = container.querySelectorAll(
            `.${styles.coin}`
        ) as NodeListOf<HTMLDivElement>

        const timeline = anime.timeline()
        const base = {
            opacity: [0, 1],
            duration: 1000,
            easing: "easeOutCubic",
            translateY: ["30%", "0"],
            rotate: ["-6deg", "0deg"],
        }
        timeline.add({ targets: layer2, ...base }, 0)
        timeline.add({ targets: layer3, ...base }, 200)
        timeline.add({ targets: contentContainer, ...base }, 400)
        timeline.add({ targets: label, ...base }, 600)

        timeline.add(
            {
                targets: coins,
                scale: [1.2, 1],
                duration: 800,
                delay: anime.stagger(200),
                easing: "easeOutBounce",
            },
            500
        )
        timeline.add(
            {
                targets: coins,
                opacity: [0, 1],
                duration: 400,
                delay: anime.stagger(200),
                easing: "easeOutCubic",
            },
            500
        )
        // timeline.add(
        //     {
        //         targets: buttonGroup,
        //         opacity: [0, 1],
        //         translateY: ["50%", "0"],
        //         translateX: ["-50%", "-50%"],
        //         duration: 1000,
        //         easing: "easeOutCubic",
        //         complete: () => {
        //             buttonGroup.style.pointerEvents = "auto"
        //         },
        //     },
        //     1600
        // )
        return timeline.finished
    },
    exit: async (container: HTMLDivElement) => {
        return anime({
            targets: container,
            opacity: [1, 0],
            filter: ["blur(0px)", "blur(10px)"],
            scale: [1, 0.9],
            duration: 500,
            easing: "easeInCubic",
        }).finished
    },
    initRecord: async (
        container: HTMLDivElement,
        row: number,
        column: number
    ) => {
        const stamps = container.querySelectorAll(
            `.${styles.stamp}`
        ) as NodeListOf<HTMLDivElement>
        const contentItems = container.querySelectorAll(
            `.${styles.contentItem}`
        ) as NodeListOf<HTMLDivElement>
        const itemToActive = contentItems[row * 7 + column]
        const stampToActive = stamps[row]
        const timeline = anime.timeline()
        timeline.add({
            duration: 1500,
            begin: () => {
                SFX.play("clip")
                itemToActive.classList.add(styles.active)
            },
        })
        timeline.add({
            duration: 1200,
            begin: () => {
                SFX.play("stamp")
                stampToActive.classList.add(styles.active)
            },
        })
        return timeline.finished
    },
    showButton: async (container: HTMLDivElement) => {
        const buttonGroup = container.querySelector(
            `.${styles.buttonGroup}`
        ) as HTMLDivElement
        const timeline = anime.timeline()
        timeline.add(
            {
                targets: buttonGroup,
                opacity: [0, 1],
                translateY: ["50%", "0"],
                translateX: ["-50%", "-50%"],
                duration: 1000,
                easing: "easeOutCubic",
                complete: () => {
                    buttonGroup.style.pointerEvents = "auto"
                },
            },
            0
        )
        return timeline.finished
    },
}

const ContentItem: React.FC<{
    className?: string
    style?: React.CSSProperties
    text: string
    open: boolean
}> = ({ className, style, text, open }) => {
    const displayText = text === "ina" ? "in a" : text
    return (
        <div
            className={classNames(
                styles.contentItem,
                open && styles.open,
                // text.length > 3 && styles.small,
                // text.length > 6 && styles.mini,
                className
            )}
            style={style}
        >
            <span className={styles.char}>{displayText}</span>
        </div>
    )
}

const AWARD_IDS = ["GOLD", "COMP", "CARD_EXP"]

const GameSceneRecord: React.FC<{
    className?: string
    style?: React.CSSProperties
}> = ({ className, style }) => {
    const {
        setScene,
        gameResult,
        resetGameResult,
        refreshInfo,
        info: { signInRecord = [] },
    } = useGameStore()
    const lockRef = useRef(false)
    const needSignIn = useMemo(() => {
        if (signInRecord.length >= 7) return false
        const lastSignIn =
            signInRecord.slice().sort((a, b) => b.ts - a.ts)[0]?.ts ?? 0
        if (lastSignIn) {
            const startOfDay = getStartOfDay()
            if (lastSignIn < startOfDay) {
                lockRef.current = true
                return true
            } else {
                return false
            }
        }
        lockRef.current = false
        return true
    }, []) // 只在初始化时执行1次
    const { t } = useTranslation()
    const [isPresent, safeToRemove] = usePresence()
    const containerRef = useRef<HTMLDivElement>(null)
    const showBoxButton = useShowBoxButton()
    useEffect(() => {
        if (!containerRef.current) return
        if (isPresent) {
            showBoxButton(false)
            if (needSignIn) {
                SCENE_ANIMATION.animate(containerRef.current)
            } else {
                SCENE_ANIMATION.animate(containerRef.current).then(() => {
                    SCENE_ANIMATION.showButton(containerRef.current!)
                })
            }
        } else {
            SCENE_ANIMATION.exit(containerRef.current).then(() => {
                // console.log("exit")
                showBoxButton(true)
                safeToRemove()
            })
        }
    }, [isPresent])

    const callResultModal = useResultModal()
    useEffect(() => {
        if (needSignIn) {
            const awardId = gameResult?.result.awardId
            if (!awardId) return
            const row = AWARD_IDS.indexOf(awardId)
            if (row === -1) return

            const p = sleep(800) // 动画时间轴控制
            gameSignIn(awardId)
                .then(async (res) => {
                    if (res.code === 0) {
                        await p
                        await SCENE_ANIMATION.initRecord(
                            containerRef.current!,
                            row,
                            signInRecord.length
                        )
                        refreshInfo()
                        const special = gameResult?.result.special
                        callResultModal(
                            {
                                special,
                                title: special
                                    ? t("game.modal.reward.title.special")
                                    : t("game.modal.reward.title.normal"),
                                titleEn: special
                                    ? t("game.modal.reward.title.special.en")
                                    : t("game.modal.reward.title.normal.en"),
                                icon: awardId.toLowerCase(),
                                content: t("game.modal.reward.content").replace(
                                    "$0",
                                    t(
                                        `game.record.gift.${awardId.toLowerCase()}`
                                    ) ?? ""
                                ),
                            },
                            () => {
                                SCENE_ANIMATION.showButton(
                                    containerRef.current!
                                )
                            }
                        )
                        lockRef.current = false
                    } else {
                        Toast.message({ content: res.msg })
                        SCENE_ANIMATION.showButton(containerRef.current!)
                        lockRef.current = false
                    }
                })
                .catch((e) => {
                    console.log(e)
                    Toast.message({ content: t("networkError") })
                    lockRef.current = false
                })
        }
    }, [])
    const currentStamp = useMemo(() => {
        if (needSignIn) {
            return undefined
        } else {
            return AWARD_IDS.findIndex(
                (id) =>
                    id ===
                    signInRecord.slice().sort((a, b) => b.index - a.index)[0]
                        .rewardId
            )
        }
    }, [])

    // const lock = useRef(false)
    // const checkSignIn = useCallback(() => {
    //     if (lock.current) return
    //     lock.current = true
    //     gameSignIn("COMP")
    //         .then((res) => {
    //             console.log(res)
    //         })
    //         .catch((err) => {})
    // }, [])
    return (
        <div
            className={classNames(styles.sceneContainer, className)}
            style={style}
            ref={containerRef}
        >
            <div className={styles.mainFrame}>
                <div className={styles.layer2}></div>
                <div className={styles.layer3}>
                    <picture>
                        <source srcSet={layer3WebP} type="image/webp" />
                        <img src={layer3Png} alt="layer3" />
                    </picture>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.card}>
                        <div className={styles.itemTitle}>
                            <div className={styles.item}>
                                <div className={styles.coin}></div>
                                <div className={styles.itemLabel}>
                                    {t("game.record.gift.gold")}
                                </div>
                            </div>
                            <div className={styles.item}>
                                <div className={styles.comp}></div>
                                <div
                                    className={classNames(
                                        styles.itemLabel,
                                        styles.small
                                    )}
                                >
                                    {t("game.record.gift.comp")}
                                </div>
                            </div>
                            <div className={styles.item}>
                                <div className={styles.expCard}></div>
                                <div
                                    className={classNames(
                                        styles.itemLabel,
                                        styles.small
                                    )}
                                >
                                    {t("game.record.gift.card_exp")}
                                </div>
                            </div>
                        </div>
                        <div className={styles.dateTitle}>
                            {t("number")
                                .split("")
                                .slice(0, 7)
                                .map((c, index) => (
                                    <div key={index} className={styles.item}>
                                        {t("game.record.date").replace("$", c)}
                                    </div>
                                ))}
                        </div>
                        <div className={styles.stampContainer}>
                            <div className={styles.item}>
                                <div
                                    className={classNames(
                                        styles.stamp,
                                        currentStamp === 0 && styles.active
                                    )}
                                ></div>
                            </div>
                            <div className={styles.item}>
                                <div
                                    className={classNames(
                                        styles.stamp,
                                        currentStamp === 1 && styles.active
                                    )}
                                ></div>
                            </div>
                            <div className={styles.item}>
                                <div
                                    className={classNames(
                                        styles.stamp,
                                        currentStamp === 2 && styles.active
                                    )}
                                ></div>
                            </div>
                        </div>
                        <div className={styles.content}>
                            {t("game.record.content")
                                .split("\n")
                                .map((s, row) => (
                                    <div className={styles.line} key={row}>
                                        {s
                                            .split(" ")
                                            .filter((c) => c !== " ")
                                            .slice(0, 7)
                                            .map((c, column) => {
                                                return (
                                                    <ContentItem
                                                        key={`${c}-${column}`}
                                                        text={c}
                                                        open={
                                                            signInRecord.find(
                                                                (item) =>
                                                                    item.index ===
                                                                    column + 1
                                                            )?.rewardId ===
                                                            AWARD_IDS[row]
                                                        }
                                                    />
                                                )
                                            })}
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
                <div className={styles.label}>
                    <picture>
                        <source srcSet={labelWebP} type="image/webp" />
                        <img src={labelPng} alt="label" />
                    </picture>
                </div>
                <div className={classNames(styles.coin, styles.coin1)}></div>
                <div className={classNames(styles.coin, styles.coin2)}></div>
                <div className={classNames(styles.coin, styles.coin3)}></div>
            </div>
            <div className={styles.buttonGroup}>
                <MainButton
                    className={styles.button}
                    icon="temple"
                    type="blue"
                    text={t("game.button.restart.cn")}
                    textEn={t("game.button.restart.en")}
                    mark={"jie"}
                    onClick={() => {
                        // gameGetInfo("rogue_5")
                        //     .then((res) => {
                        //         console.log(res)
                        //     })
                        //     .catch((err) => {
                        //         console.log(err)
                        //     })
                        if (lockRef.current) return
                        setScene(EGameScene.Loading)
                        resetGameResult()
                    }}
                />
            </div>
        </div>
    )
}

export default GameSceneRecord
